/**
 * Version utilities for development, testing, and debugging
 */

/**
 * Compares two version strings using semantic versioning rules
 * @param {string} version1 - First version to compare
 * @param {string} version2 - Second version to compare
 * @returns {number} - Returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
export function compareVersions(version1, version2) {
  if (!version1 || !version2) {
    return 0;
  }

  const v1Parts = version1.split(".").map(Number);
  const v2Parts = version2.split(".").map(Number);

  const maxLength = Math.max(v1Parts.length, v2Parts.length);

  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;

    if (v1Part < v2Part) return -1;
    if (v1Part > v2Part) return 1;
  }

  return 0;
}

/**
 * Validates if a version string follows semantic versioning format
 * @param {string} version - Version string to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export function isValidVersion(version) {
  if (!version || typeof version !== "string") {
    return false;
  }

  const semverRegex =
    /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;
  return semverRegex.test(version);
}

/**
 * Increments a version string by a specified type
 * @param {string} version - Current version string
 * @param {string} type - Type of increment ('major', 'minor', 'patch')
 * @returns {string} - New incremented version
 */
export function incrementVersion(version, type = "patch") {
  if (!isValidVersion(version)) {
    throw new Error("Invalid version format");
  }

  const parts = version.split(".").map(Number);
  const [major, minor, patch] = parts;

  switch (type.toLowerCase()) {
    case "major":
      return `${major + 1}.0.0`;
    case "minor":
      return `${major}.${minor + 1}.0`;
    case "patch":
    default:
      return `${major}.${minor}.${patch + 1}`;
  }
}

/**
 * Gets version information for debugging
 * @returns {object} - Object containing version debug information
 */
export function getVersionDebugInfo() {
  const stored = localStorage.getItem("app_version");
  const userDetails = localStorage.getItem("userDetails");
  const permissions = localStorage.getItem("userPermissions");

  return {
    storedVersion: stored,
    hasUserDetails: !!userDetails,
    hasPermissions: !!permissions,
    timestamp: new Date().toISOString(),
    localStorage: {
      keys: Object.keys(localStorage),
      totalItems: localStorage.length,
    },
  };
}

/**
 * Simulates a version change for testing purposes
 * @param {string} newVersion - New version to simulate
 */
export function simulateVersionChange(newVersion) {
  if (!isValidVersion(newVersion)) {
    console.error("Invalid version format provided for simulation");
    return;
  }

  console.log(`🔄 Simulating version change to: ${newVersion}`);

  // Store the new version in localStorage
  localStorage.setItem("app_version", newVersion);

  // Also update the version.json file in memory (for testing)
  if (window.__VERSION_OVERRIDE__) {
    window.__VERSION_OVERRIDE__ = newVersion;
  }

  console.log(`✅ Version simulation complete. Stored version: ${newVersion}`);
  console.log("💡 Refresh the page to trigger version check and logout");
}

/**
 * Resets version-related localStorage for testing
 */
export function resetVersionForTesting() {
  localStorage.removeItem("app_version");
  console.log("🧹 Version data cleared for testing");
}

/**
 * Logs version change history (useful for debugging)
 * @param {string} oldVersion - Previous version
 * @param {string} newVersion - New version
 * @param {string} action - Action taken ('logout', 'update', etc.)
 */
export function logVersionChange(oldVersion, newVersion, action = "update") {
  const logEntry = {
    timestamp: new Date().toISOString(),
    oldVersion,
    newVersion,
    action,
    userAgent: navigator.userAgent,
    location: window.location.href,
  };

  // Store in sessionStorage for debugging (won't persist across sessions)
  const existingLogs = JSON.parse(
    sessionStorage.getItem("version_change_log") || "[]",
  );
  existingLogs.push(logEntry);

  // Keep only last 10 entries
  const recentLogs = existingLogs.slice(-10);
  sessionStorage.setItem("version_change_log", JSON.stringify(recentLogs));

  console.log("📋 Version change logged:", logEntry);
}

/**
 * Gets the version change log for debugging
 * @returns {array} - Array of version change log entries
 */
export function getVersionChangeLog() {
  return JSON.parse(sessionStorage.getItem("version_change_log") || "[]");
}

/**
 * Clears the version change log
 */
export function clearVersionChangeLog() {
  sessionStorage.removeItem("version_change_log");
  console.log("🧹 Version change log cleared");
}

/**
 * Development utility to force a specific version for testing
 * Only works in development environment
 * @param {string} version - Version to force
 */
export function forceVersionForDevelopment(version) {
  if (process.env.NODE_ENV !== "development") {
    console.warn(
      "forceVersionForDevelopment only works in development environment",
    );
    return;
  }

  if (!isValidVersion(version)) {
    console.error("Invalid version format provided");
    return;
  }

  // Override the fetch function temporarily
  window.__VERSION_OVERRIDE__ = version;
  console.log(`🔧 Development: Forced version to ${version}`);
  console.log("💡 This override will be used until page refresh");
}

/**
 * Checks if version override is active (for development)
 * @returns {string|null} - Overridden version or null
 */
export function getVersionOverride() {
  return window.__VERSION_OVERRIDE__ || null;
}
